import React, { useState, useEffect, useMemo, useCallback } from "react";
import { getLastResults, getDetailResults } from "@/services/casinoApi";
import ResultModalLayout from "@/components/ResultModalLayout";
import "./Result.css";

// Helper function to get card image path
const getCardImage = (cardCode) => {
  if (!cardCode) return null;
  try {
    const cleanCode = cardCode.replace(/\.(jpg|png)$/i, '').trim();
    return `/assets/img/cards/${cleanCode}.png`;
  } catch (e) {
    return null;
  }
};

// Helper function to parse card string and split into Dragon, Tiger, and Lion cards
const parseCards = (cardString) => {
  if (!cardString) return { dragon: null, tiger: null, lion: null };
  
  const cards = cardString.split(',').map(c => c.trim());
  const dragonCard = cards[0] || null;
  const tigerCard = cards[1] || null;
  const lionCard = cards[2] || null;
  
  return { 
    dragon: dragonCard,
    tiger: tigerCard,
    lion: lionCard
  };
};

// Helper function to parse rdesc: "Lion#No#..." or similar format
const parseRdesc = (rdesc) => {
  if (!rdesc) return { winner: "" };
  
  const parts = rdesc.split('#');
  const winner = parts[0] || "";
  
  return {
    winner: winner.trim(),
  };
};

export default function ResultDragonLion({ data = [], gameData = {} }) {
  const [detailResult, setDetailResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastResults, setLastResults] = useState([]);

  const gameType = "dtl20";

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(gameType);
        if (response?.success) {
          if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
            const mappedResults = response.data.data.res.map(item => ({
              mid: item.mid,
              win: item.win === "1" ? "D" : item.win === "2" ? "T" : item.win === "3" ? "L" : item.win
            }));
            setLastResults(mappedResults);
          } else if (response?.data?.res && Array.isArray(response.data.res)) {
            const mappedResults = response.data.res.map(item => ({
              mid: item.mid,
              win: item.win === "1" ? "D" : item.win === "2" ? "T" : item.win === "3" ? "L" : item.win
            }));
            setLastResults(mappedResults);
          }
        }
      } catch (error) {
        console.error("Error fetching last results:", error);
      }
    };
    
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [gameType]);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);
    const mid = resultItem?.mid || resultItem;
    
    if (mid) {
      try {
        const response = await getDetailResults(gameType, mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            setDetailResult(response.data.data.t1);
          } else if (response?.data?.t1) {
            setDetailResult(response.data.t1);
          } else if (response?.data) {
            setDetailResult(response.data);
          }
        }
      } catch (error) {
        console.error("Error fetching detail result:", error);
      }
    }
    setLoading(false);
    setShowModal(true);
  }, [gameType]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  const getResultClass = (res) => {
    const resStr = typeof res === 'object' ? res.win : res;
    if (resStr === "D" || resStr === "d" || resStr === "Dragon" || resStr === "1") return "result-d";
    if (resStr === "T" || resStr === "t" || resStr === "Tiger" || resStr === "2") return "result-t";
    if (resStr === "L" || resStr === "l" || resStr === "Lion" || resStr === "3") return "result-l";
    return "result-default";
  };

  const getResultLabel = (res) => {
    const resStr = typeof res === 'object' ? res.win : res;
    if (resStr === "D" || resStr === "d" || resStr === "Dragon" || resStr === "1") return "D";
    if (resStr === "T" || resStr === "t" || resStr === "Tiger" || resStr === "2") return "T";
    if (resStr === "L" || resStr === "l" || resStr === "Lion" || resStr === "3") return "L";
    return resStr;
  };

  // Format modal data
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal || !detailResult) return null;

    const cards = parseCards(detailResult.card);
    const rdescData = parseRdesc(detailResult.rdesc);
    const winner = detailResult.winnat || rdescData.winner || "";
    const winValue = detailResult.win;
    
    // Determine which side is the winner
    const isDragonWinner = winValue === "1" || winner.toLowerCase() === "dragon";
    const isTigerWinner = winValue === "2" || winner.toLowerCase() === "tiger";
    const isLionWinner = winValue === "3" || winner.toLowerCase() === "lion";

    return {
      roundId: detailResult.rid || detailResult.mid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      dragon: {
        card: cards.dragon,
        isWinner: isDragonWinner,
      },
      tiger: {
        card: cards.tiger,
        isWinner: isTigerWinner,
      },
      lion: {
        card: cards.lion,
        isWinner: isLionWinner,
      },
      winner: winner,
    };
  }, [selectedResult, detailResult, showModal, gameData]);

  // Combine API results with fallback data
  const allResults = useMemo(() => {
    if (lastResults.length > 0) {
      return lastResults;
    }
    // Fallback to data prop, mapping it to the expected format
    return data.map((res, idx) => {
      if (typeof res === 'object' && res.win) {
        return { win: res.win, mid: res.mid || null };
      }
      // Handle string values
      const winValue = typeof res === 'string' ? res : String(res);
      return { 
        win: winValue === "1" ? "D" : winValue === "2" ? "T" : winValue === "3" ? "L" : winValue,
        mid: null 
      };
    });
  }, [lastResults, data]);

  return (
    <>
      <div className="result-row">
        {allResults.map((resultItem, index) => {
          const win = resultItem.win;
          const isDragon = win === "D" || win === "1" || win === "d" || win === "Dragon";
          const isTiger = win === "T" || win === "2" || win === "t" || win === "Tiger";
          const isLion = win === "L" || win === "3" || win === "l" || win === "Lion";
          
          return (
            <div
              key={`${resultItem.mid || index}-${index}`}
              className={`result-circle ${getResultClass(resultItem)}`}
              onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
              style={{ 
                cursor: resultItem.mid ? 'pointer' : 'default'
              }}
            >
              {getResultLabel(resultItem)}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Dragon Tiger Lion Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <h6 className="text-right">
                  <b>Round Id :</b> {modalData.roundId}
                </h6>
              </div>
            </div>
            <div className="row bottom-border-casino">
              <div className="col-md-12">
                <div className="player-image-container row">
                  <div className="col-md-4 sixplayer-image result-dragon-lion-header">
                    <h6 className="text-center">Dragon</h6>
                    <div className="text-center">
                      {modalData.dragon.card && (
                        <img
                          src={getCardImage(modalData.dragon.card)}
                          alt="Dragon Card"
                          onError={(e) => {
                            e.target.src = "/assets/img/cards/1.png";
                          }}
                        />
                      )}
                      {modalData.dragon.isWinner && (
                        <div className="text-center">
                          <div className="winner-label bg-success m-t-10">
                            <i className="fas fa-trophy mr-2"></i>Winner
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4 sixplayer-image result-dragon-lion-header">
                    <h6 className="text-center">Tiger</h6>
                    <div className="text-center">
                      {modalData.tiger.card && (
                        <img
                          src={getCardImage(modalData.tiger.card)}
                          alt="Tiger Card"
                          onError={(e) => {
                            e.target.src = "/assets/img/cards/1.png";
                          }}
                        />
                      )}
                      {modalData.tiger.isWinner && (
                        <div className="text-center">
                          <div className="winner-label bg-success m-t-10">
                            <i className="fas fa-trophy mr-2"></i>Winner
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4 sixplayer-image result-dragon-lion-header">
                    <h6 className="text-center">Lion</h6>
                    <div className="text-center">
                      {modalData.lion.card && (
                        <img
                          src={getCardImage(modalData.lion.card)}
                          alt="Lion Card"
                          onError={(e) => {
                            e.target.src = "/assets/img/cards/1.png";
                          }}
                        />
                      )}
                      {modalData.lion.isWinner && (
                        <div className="text-center">
                          <div className="winner-label bg-success m-t-10">
                            <i className="fas fa-trophy mr-2"></i>Winner
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
}

